import React, { useState, useRef } from 'react';
import { importFile, confirmImport, safeErrorMessage } from '../services/api';
import Modal from './Modal';
import { HiOutlineUpload } from 'react-icons/hi';

export default function ImportModal({ show, onClose, projectId, onImported }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'preview'
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [parsed, setParsed] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const fileRef = useRef(null);

  const reset = () => {
    setStep('upload');
    setFile(null);
    setLoading(false);
    setImporting(false);
    setError('');
    setParsed(null);
    setSelected(new Set());
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await importFile(projectId, file);
      setParsed(data);
      const allIndices = new Set(data.exigences.map((_, i) => i));
      setSelected(allIndices);
      setStep('preview');
    } catch (err) {
      setError(safeErrorMessage(err, 'Erreur lors de l\'analyse du fichier.'));
    }
    setLoading(false);
  };

  const toggleSelect = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    if (!parsed) return;
    if (selected.size === parsed.exigences.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(parsed.exigences.map((_, i) => i)));
    }
  };

  const selectedExigences = parsed ? parsed.exigences.filter((_, i) => selected.has(i)) : [];
  const selectedTcCount = selectedExigences.reduce((sum, e) => sum + (e.cas_tests?.length || 0), 0);

  const handleConfirm = async () => {
    if (selectedExigences.length === 0) return;
    setImporting(true);
    setError('');
    try {
      await confirmImport(projectId, { exigences: selectedExigences });
      handleClose();
      if (onImported) onImported();
    } catch (err) {
      setError(safeErrorMessage(err, 'Erreur lors de l\'import.'));
    }
    setImporting(false);
  };

  const footer = (
    <>
      {step === 'upload' && (
        <>
          <button className="btn btn-secondary btn-sm" onClick={handleClose}>Annuler</button>
          <button className="btn btn-primary btn-sm" onClick={handleAnalyze} disabled={!file || loading}>
            {loading ? <><span className="spinner-border spinner-border-sm me-1"></span> Analyse...</> : 'Analyser'}
          </button>
        </>
      )}
      {step === 'preview' && (
        <>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => { setStep('upload'); setError(''); }}>Retour</button>
          <button className="btn btn-secondary btn-sm" onClick={handleClose}>Annuler</button>
          <button
            className="btn btn-success btn-sm"
            onClick={handleConfirm}
            disabled={selectedExigences.length === 0 || importing}
          >
            {importing
              ? <><span className="spinner-border spinner-border-sm me-1"></span> Import...</>
              : `Importer (${selectedExigences.length} exig., ${selectedTcCount} CT)`}
          </button>
        </>
      )}
    </>
  );

  return (
    <Modal
      show={show}
      onClose={handleClose}
      title={step === 'upload' ? 'Importer un fichier' : 'Apercu de l\'import'}
      icon={<HiOutlineUpload />}
      size="lg"
      footer={footer}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {error && <div className="alert alert-danger py-2">{error}</div>}

        {step === 'upload' && (
          <div>
            <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
              Importez un fichier Excel (.xlsx) ou Word (.docx) contenant vos exigences et cas de test.
              Le format Excel doit correspondre au modele d'export (colonnes A-O).
            </p>
            <div className="mb-3">
              <input
                ref={fileRef}
                type="file"
                className="form-control form-control-sm"
                accept=".xlsx,.docx"
                onChange={(e) => {
                  const f = e.target.files[0];
                  if (f && f.size > 10 * 1024 * 1024) {
                    alert('Fichier trop volumineux (max 10 Mo).'); e.target.value = ''; setFile(null); return;
                  }
                  setFile(f);
                }}
              />
            </div>
            {file && (
              <p className="small text-muted">
                Fichier selectionne : <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} Ko)
              </p>
            )}
          </div>
        )}

        {step === 'preview' && parsed && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <span className="badge bg-primary me-2">{parsed.stats.requirements} exigences</span>
                <span className="badge bg-info">{parsed.stats.test_cases} cas de test</span>
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={toggleAll}>
                {selected.size === parsed.exigences.length ? 'Tout decocher' : 'Tout cocher'}
              </button>
            </div>

            <div className="import-preview-list">
              {parsed.exigences.map((exg, idx) => (
                <div key={idx} className={`import-preview-item ${selected.has(idx) ? '' : 'opacity-50'}`}>
                  <div className="d-flex align-items-start gap-2">
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      checked={selected.has(idx)}
                      onChange={() => toggleSelect(idx)}
                    />
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="req-id">{exg.exg_id}</span>
                        <strong className="small">{exg.exigence}</strong>
                        <span className="meta-tag">{exg.type_exigence}</span>
                        {exg.statut_validation && exg.statut_validation !== 'Brouillon' && (
                          <span className="meta-tag">{exg.statut_validation}</span>
                        )}
                      </div>
                      {exg.desc_exigence && (
                        <p className="small text-muted mb-1" style={{ fontSize: '0.78rem' }}>{exg.desc_exigence}</p>
                      )}
                      {exg.cas_tests && exg.cas_tests.length > 0 && (
                        <div className="ms-3">
                          <table className="table table-sm mb-0" style={{ fontSize: '0.75rem' }}>
                            <thead>
                              <tr>
                                <th style={{ width: '70px' }}>ID</th>
                                <th>Cas de Test</th>
                                <th>Action</th>
                                <th>Resultat</th>
                                <th style={{ width: '40px' }}>Statut</th>
                              </tr>
                            </thead>
                            <tbody>
                              {exg.cas_tests.map((ct, ci) => (
                                <tr key={ci}>
                                  <td><span className="req-id" style={{ fontSize: '0.62rem' }}>{ct.ct_id}</span></td>
                                  <td>{ct.cas_test}</td>
                                  <td>{ct.action}</td>
                                  <td>{ct.resultat_attendu}</td>
                                  <td><span className={`status-badge status-${ct.statut || 'NT'}`}>{ct.statut || 'NT'}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
